import {
  createError,
  showError,
  useCookie,
  useNuxtApp,
  useRuntimeConfig,
} from "nuxt/app";
import { unref } from "vue";

export default async function customFetch<T>(
  url: string,
  localeTmp: any = undefined,
  options: Record<string, unknown> = { method: "GET" },
  cache: RequestCache = "force-cache",
  retry: number = 0,
  currencyTmp?: string,
): Promise<T> {
  const config = useRuntimeConfig();
  // Prefer an explicit override, then the actual i18n locale (resolved from the URL
  // prefix on server + client), then the cookie/default. The cookie sync plugin is
  // client-only, so on SSR the cookie may still hold the default locale.
  const i18nLocale = unref((useNuxtApp().$i18n as any)?.locale) as
    | string
    | undefined;
  const locale =
    localeTmp ||
    i18nLocale ||
    useCookie("locale").value ||
    config.public.defaultLocale;
  const currency =
    currencyTmp || useCookie("currency").value || config.public.defaultCurrency;

  // Define headers
  const headers = {
    "X-CLIENT-ID": config.public.xClientId,
    Accept: "application/json",
    "Accept-Language": locale,
    "Sec-Fetch-Dest": "empty",
    "Sec-Fetch-Mode": "cors",
    "Sec-Fetch-Site": "same-site",
    "Accept-Currency": currency,
  } as Record<string, string>;

  // Base URL
  const baseURL = config.public.apiBaseUrl!.toString();

  // Construct the full URL
  const fullURL = baseURL?.endsWith("/") ? baseURL + url : baseURL + "/" + url;

  const maxRetries = retry || 0;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Use $fetch with caching
      const response: any = await $fetch<T>(fullURL, {
        ...options,
        headers,
        cache: cache,
      });

      return response.data;
    } catch (error: any) {
      if (attempt < maxRetries) {
        const delay = 1000 * (attempt + 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      showError(error);
      throw createError({
        message: `API request failed: ${error.message}`,
        statusCode: error.statusCode || 500,
      });
    }
  }

  return null as T;
}
